import React, { Component } from 'react';
import css from '../../css/module/er-diagram.css';
import MapsTable from '../../util/MapsTable';
import DraggableList from '../draggable-list/DraggableList';

export const TITLE_HEIGHT = 30; // px
export const FIELD_HEIGHT = 26; // px
const FONT_SIZE = 1;

const STRAIGHT_LINES = false;

function height(visibleColumns, allColumns) {
  return (
    TITLE_HEIGHT +
    visibleColumns.length * FIELD_HEIGHT +
    (allColumns.length > visibleColumns.length ? FIELD_HEIGHT : 0)
  );
}

const COLOURS = [
  'rgb(54, 162, 235)', // blue
  'rgb(255, 205, 86)', // yellow
  'rgb(255, 99, 132)', // red
  'rgb(75, 192, 192)', // green
  'rgb(255, 159, 64)', // orange
  'rgb(153, 102, 255)', // purple
];
const arrows = {};
const ARROW_PREFIX = 'arrow-active-';
COLOURS.map((colour, i) => {
  arrows[colour] = ARROW_PREFIX + i;
});

class Map extends Component {
  constructor(props) {
    super(props);

    const {
      allTables, // list of SchemaTable objs
      visibleTables, // list of MapsTable objs
    } = props;

    this._boxes = []; // DOM elements
    this._tableIndexByName = {}; // e.g. public.customers => 2

    //
    // prepare visible tables
    //
    const boxes = [];
    const tables = [];
    allTables.map((table) => {
      const mapsTable = visibleTables.find(
        (mt) => mt.schema() === table.schema() && mt.table() === table.table(),
      );
      if (!mapsTable) {
        // table is not visible, so skip
        return;
      }
      // create a box on the diagram for this table
      boxes.push({
        x: mapsTable.x(),
        y: mapsTable.y(),
        visibleColumns: mapsTable.visibleColumns(),
        width: 100,
        height: height(mapsTable.visibleColumns(), table.columns()),
        color: '#fff',
      });
      // create a lookup for tables (for performance during rendering)
      this._tableIndexByName[`${table.schema()}.${table.table()}`] =
        tables.length; // length here is actually the position index of the table
      // store the table information (name and columns) for display
      tables.push(table);
    });

    // create simplified version of tables, with just the columns we want to show in the diagram (e.g. primary and foreign keys)

    this.state = {
      boxes, // { x, y, width, height, visibleColumns }
      tables, // list of visible SchemaTable objs
      joins: this.props.joins, // list of Pair(ColumnRef, ColumnRef)
      dragPointX: 0,
      dragPointY: 0,
      positionChanged: false,
      editing: -1, // index of box in edit mode
      selected: -1, // index of selected box (for dragging)
      hovered: -1, // index of box currently being hovered over
    };
  }

  shouldComponentUpdate(nextProps, nextState) {
    // for performance reasons we only update the map when the state changes
    // (to update from outside, just change 'key' property)
    if (this.state === nextState) {
      return false;
    }
    return true;
  }

  componentDidMount() {
    this.updateWidths();

    const { boxes } = this.state;
    let { top, left } = this.props.scrollPosition;

    // when first opening image the top and left are set to -1
    if (top < 0 || left < 0) {
      top = 1000;
      left = 1000;
      if (boxes.length > 0) {
        // identify correct position to start (so that viewport is in middle of existing diagram)
        const avgX =
          boxes.map((box) => box.x + box.width / 2).reduce((a, b) => a + b, 0) /
          boxes.length;
        const avgY =
          boxes
            .map((box) => box.y + box.height / 2)
            .reduce((a, b) => a + b, 0) / boxes.length;
        const rect = this._wrapper.getBoundingClientRect();
        top = avgY * this.props.scale - rect.height / 2;
        left = avgX * this.props.scale - rect.width / 2;
      }
      // jump to correct position
      this._wrapper.scrollTop = top;
      this._wrapper.scrollLeft = left;
      // let parent know
      this.onScroll();
    } else {
      // scroll to correct position
      this._wrapper.scrollTop = top;
      this._wrapper.scrollLeft = left;
    }
  }

  onMouseDown(e, boxIndex) {
    const offset = this._boxes[boxIndex].getBoundingClientRect();
    this.setState({
      selected: boxIndex,
      editing: -1, // reset
      dragPointX: e.clientX - offset.left,
      dragPointY: e.clientY - offset.top,
    });
  }

  fireChanged() {
    const { onChange } = this.props;
    if (onChange) {
      // use timeout to make sure that state changes have completed
      setTimeout(() => {
        const updated = this.state.boxes.map((box, i) => {
          const table = this.state.tables[i];
          return new MapsTable(
            table.schema(),
            table.table(),
            box.x,
            box.y,
            box.visibleColumns,
          );
        });
        onChange(updated);
      }, 1);
    }
  }

  onMouseUp() {
    if (this.state.selected > -1 && this.state.positionChanged) {
      // diagram updated, so fire onChange handler
      this.fireChanged();
    }
    this.setState({
      positionChanged: false,
      selected: -1,
    });
  }

  onMouseMove(e) {
    if (this.state.selected > -1) {
      const container = this._container.getBoundingClientRect();
      // eslint-disable-next-line react/no-access-state-in-setstate
      const boxes = this.state.boxes.slice();
      const x = e.clientX - container.left - this.state.dragPointX;
      const y = e.clientY - container.top - this.state.dragPointY;
      const box = boxes[this.state.selected];
      boxes[this.state.selected] = {
        ...box,
        x: x / this.props.scale,
        y: Math.min(this.props.gridHeight - box.height, y / this.props.scale),
      };
      this.setState({
        positionChanged: true,
        boxes,
      });

      if (!this.props.demo) {
        // if user gets close to edge, scroll
        const mapViewport = this._wrapper.getBoundingClientRect(); // top, bottom, left, right
        if (
          e.clientX > mapViewport.right - 100 &&
          this._wrapper.scrollLeft < container.width - mapViewport.width
        ) {
          this._wrapper.scrollLeft += 10; // scroll right
        } else if (e.clientX < mapViewport.left + 100) {
          this._wrapper.scrollLeft -= 10;
        } else if (e.clientY < mapViewport.top + 100) {
          this._wrapper.scrollTop -= 10;
        } else if (
          e.clientY > mapViewport.bottom - 100 &&
          this._wrapper.scrollTop < container.height - mapViewport.height
        ) {
          this._wrapper.scrollTop += 10; // scroll down
        }
      }
    }
  }

  columnsChanged(boxIndex, items) {
    const { boxes, tables } = this.state;
    const box = boxes[boxIndex];
    const table = tables[boxIndex];
    this.setState({
      boxes: [
        ...boxes.slice(0, boxIndex),
        {
          ...box,
          visibleColumns: items,
          height: height(items, table.columns()),
        },
        ...boxes.slice(boxIndex + 1),
      ],
    });
    this.fireChanged();
    this.updateWidths();
  }

  renderBoxes(columnsToHighlight) {
    const { tables, joins } = this.state;

    let currentColor = -1;
    const nextColor = () => {
      currentColor = (currentColor + 1) % COLOURS.length;
      return COLOURS[currentColor];
    };

    // identify columns that should be highlighted
    joins.map(([a, b]) => {
      const indexA = this._tableIndexByName[a.tov().uniqueName()];
      const indexB = this._tableIndexByName[b.tov().uniqueName()];
      if (indexA === this.state.hovered || indexB === this.state.hovered) {
        // if both columns are visible, highlight them
        if (
          this.state.boxes[indexA] &&
          this.state.boxes[indexB] &&
          this.state.boxes[indexA].visibleColumns.indexOf(a.name()) > -1 &&
          this.state.boxes[indexB].visibleColumns.indexOf(b.name()) > -1
        ) {
          // give them a colour
          const keyA = a.toString();
          const keyB = b.toString();
          const colour =
            columnsToHighlight[keyA] || columnsToHighlight[keyB] || nextColor();
          columnsToHighlight[keyA] = colour;
          columnsToHighlight[keyB] = colour;
        }
      }
    });

    return this.state.boxes.map((box, boxIndex) => {
      const table = tables[boxIndex];

      const onEditClicked = () => {
        clearTimeout(this._leaveTimeout);
        this.setState({
          editing: boxIndex,
        });
        this.updateWidths();
      };

      let isHighlighted = this.state.hovered === boxIndex;
      let fields = box.visibleColumns.map((name, i) => {
        const highlight = columnsToHighlight[`${table.toString()}.${name}`];
        isHighlighted = isHighlighted || highlight;
        return (
          <div
            key={i}
            onClick={onEditClicked}
            className={`${css.field} ${highlight ? css.highlightField : ''}`}
            style={{ height: `${FIELD_HEIGHT}px`, backgroundColor: highlight }}
          >
            {name}
          </div>
        );
      });

      const visibleFields = box.visibleColumns.length;
      const hiddenFields = table.columns().length - visibleFields;

      if (hiddenFields > 0) {
        const lastRow = (
          <div
            key="hidden-fields"
            onClick={onEditClicked}
            className={css.field}
            style={{ height: `${FIELD_HEIGHT}px` }}
          >
            <span style={{ opacity: 0.5 }}>
              +{hiddenFields} hidden field{hiddenFields > 1 ? 's' : ''}
            </span>
          </div>
        );
        fields.push(lastRow);
      }

      if (this.state.editing === boxIndex) {
        const allFields = [
          ...box.visibleColumns,
          ...table
            .columns()
            .map((c) => c.name())
            .filter((name) => box.visibleColumns.indexOf(name) === -1),
        ];
        fields = (
          <DraggableList
            items={allFields}
            visibleItems={box.visibleColumns.length}
            onChange={(items) => this.columnsChanged(boxIndex, items)}
          />
        );
      }

      const onMouseLeave = () => {
        this._leaveTimeout = setTimeout(() => {
          this.setState({
            hovered: -1,
            editing: -1,
          });
          this.updateWidths();
        }, 300);
      };

      const style = {
        left: `${box.x}px`,
        top: `${box.y}px`,
        opacity: this.state.hovered > -1 && !isHighlighted ? 0.5 : 1.0,
        zIndex:
          this.state.editing === boxIndex
            ? 3 // this box is being edited
            : this.state.selected === boxIndex
            ? 2 // this box is being dragged
            : this.state.selected === -1 && isHighlighted
            ? 1 // no box is being dragged, but this box is being highlighted
            : undefined,
        fontSize: `${FONT_SIZE}em`,
      };

      const nothingHovered = this.state.hovered === -1;

      const doHover = () => {
        clearTimeout(this._leaveTimeout);
        this.setState({ hovered: boxIndex });
      };

      return (
        <div
          key={boxIndex}
          onMouseEnter={doHover}
          onTouchStart={doHover} /* for mobile */
          onMouseLeave={onMouseLeave}
          ref={(div) => (this._boxes[boxIndex] = div)}
          className={`${css.box} ${
            boxIndex === this.state.selected ? css.selectedBox : ''
          } ${boxIndex === this.state.hovered ? css.hoveredBox : ''}`}
          style={style}
        >
          <div style={{ backgroundColor: box.color, borderRadius: '4px' }}>
            <div
              className={css.boxTitle}
              style={{
                height: `${TITLE_HEIGHT}px`,
                backgroundColor:
                  nothingHovered || isHighlighted ? '#061e2d' : undefined,
                color: nothingHovered || isHighlighted ? '#fff' : undefined,
              }}
              onTouchStart={(e) =>
                this.onMouseDown(e.touches[0], boxIndex)
              } /* for mobile */
              onMouseDown={(e) => this.onMouseDown(e, boxIndex)}
            >
              <div className={css.boxControlLeft} />
              <div style={{ paddingRight: '5px', paddingLeft: '5px' }}>
                {table.table()}
              </div>
              {this.props.demo ? (
                <div className={css.boxControlLeft} />
              ) : (
                this.props.dropdownBuilder(table, onEditClicked)
              )}
            </div>
            {fields}
          </div>
        </div>
      );
    });
  }

  /*
   * Positions lines on the edge of the boxes
   */
  pos(startBox, endBox, reverse) {
    const start = [startBox.x, startBox.y];
    const end = [endBox.x, endBox.y];
    const startDims = [startBox.width, startBox.height];
    const endDims = [endBox.width, endBox.height];
    const x = reverse ? 1 : 0;
    const y = reverse ? 0 : 1;

    const startOrigin = [
      start[x] + startDims[x] / 2,
      start[y] + startDims[y] / 2,
    ];
    const endOrigin = [end[x] + endDims[x] / 2, end[y] + endDims[y] / 2];

    const right =
      (startDims[y] / 2 / Math.abs(endOrigin[1] - startOrigin[1])) *
      (endOrigin[0] - startOrigin[0]);
    const result = startOrigin[0] + right;
    return Math.min(start[x] + startDims[x], Math.max(start[x], result));
  }

  renderLines(columnsToHighlight) {
    const { tables, joins } = this.state;

    const columnIndex = (box, columnRef) => {
      return box.visibleColumns.findIndex(
        (cName) => cName === columnRef.name(),
      );
    };

    return joins.map(([a, b], key) => {
      const indexA = this._tableIndexByName[a.tov().uniqueName()];
      const indexB = this._tableIndexByName[b.tov().uniqueName()];
      const startBox = this.state.boxes[indexA];
      const endBox = this.state.boxes[indexB];
      const startTable = tables[indexA];
      const endTable = tables[indexB];
      if (!startTable || !endTable) {
        return null; // table isn't visible
      }
      const startTableColumnIndex = columnIndex(startBox, a);
      const endTableColumnIndex = columnIndex(endBox, b);
      if (startTableColumnIndex === -1 || endTableColumnIndex === -1) {
        return null; // column isn't visible (we only show foreign keys lines for columns that are visible)
      }

      const lineStart = [
        this.pos(startBox, endBox, false),
        this.pos(startBox, endBox, true),
      ];
      const lineEnd = [
        this.pos(endBox, startBox, false),
        this.pos(endBox, startBox, true),
      ];

      const startIsVertical =
        lineStart[1] === startBox.y ||
        lineStart[1] === startBox.y + startBox.height;
      const endIsVertical =
        lineEnd[1] === endBox.y || lineEnd[1] === endBox.y + endBox.height;

      const RATIO = 0.2;
      let curveOffsetStart;
      let curveOffsetEnd;
      const xDistBetween = lineEnd[0] - lineStart[0];
      const yDistBetween = lineEnd[1] - lineStart[1];
      if (startIsVertical && endIsVertical) {
        curveOffsetStart = [0, RATIO * yDistBetween];
        curveOffsetEnd = [0, (1 - RATIO) * yDistBetween * -1];
      } else if (!startIsVertical && !endIsVertical) {
        curveOffsetStart = [RATIO * xDistBetween, 0];
        curveOffsetEnd = [(1 - RATIO) * xDistBetween * -1, 0];
      } else if (startIsVertical && !endIsVertical) {
        curveOffsetStart = [0, yDistBetween];
        curveOffsetEnd = [xDistBetween * -1, 0];
      } else {
        curveOffsetStart = [xDistBetween, 0];
        curveOffsetEnd = [0, yDistBetween * -1];
      }

      const curveStart = [
        lineStart[0] + curveOffsetStart[0],
        lineStart[1] + curveOffsetStart[1],
      ];
      const curveEnd = [
        lineEnd[0] + curveOffsetEnd[0],
        lineEnd[1] + curveOffsetEnd[1],
      ];

      const highlightColour = columnsToHighlight[a.toString()];
      const lineColour =
        indexA === this.state.hovered || indexB === this.state.hovered
          ? highlightColour
          : '#CCC';
      const arrow =
        indexA === this.state.hovered || indexB === this.state.hovered
          ? `url(#${arrows[highlightColour]})`
          : 'url(#arrow-inactive)';
      const lineClass =
        indexA === this.state.hovered || indexB === this.state.hovered
          ? css.activeLine
          : css.inactiveLine;

      // see Bezier curves: https://developer.mozilla.org/en-US/docs/Web/SVG/Tutorial/Paths
      const d = STRAIGHT_LINES
        ? `M ${lineStart[0]} ${lineStart[1]} L ${curveStart[0]} ${curveStart[1]} L ${curveEnd[0]} ${curveEnd[1]} L ${lineEnd[0]} ${lineEnd[1]}`
        : `M ${lineStart[0]} ${lineStart[1]} C ${curveStart[0]} ${curveStart[1]} ${curveEnd[0]} ${curveEnd[1]} ${lineEnd[0]} ${lineEnd[1]}`;
      return (
        <path
          className={lineClass}
          key={key}
          markerEnd={arrow}
          stroke={lineColour}
          strokeWidth={2}
          fill="none"
          d={d}
        />
      );
    });
  }

  updateWidths() {
    setTimeout(() => {
      const newBoxes = this.state.boxes.slice();
      let updated = false;
      this.state.boxes.map((box, i) => {
        const el = this._boxes[i];
        if (!el) {
          return;
        }
        const rect = el.getBoundingClientRect();
        // if width has changed, make a note of it (we need it for calculating arrow positions)
        if (box.width !== rect.width) {
          newBoxes[i] = { ...box, width: rect.width / this.props.scale };
          updated = true;
        }
      });
      if (updated) {
        this.setState({
          boxes: newBoxes,
        });
      }
    }, 1);
  }

  renderArrows() {
    return (
      <defs>
        <marker
          id="arrow-inactive"
          markerWidth="10"
          markerHeight="10"
          refX="8"
          refY="3"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path d="M0,0 L0,6 L9,3 z" fill="#CCC" />
        </marker>
        {COLOURS.map((colour, colourIndex) => {
          return (
            <marker
              key={colourIndex}
              id={ARROW_PREFIX + colourIndex}
              markerWidth="10"
              markerHeight="10"
              refX="8"
              refY="3"
              orient="auto"
              markerUnits="strokeWidth"
            >
              <path d="M0,0 L0,6 L9,3 z" fill={colour} />
            </marker>
          );
        })}
      </defs>
    );
  }

  onScroll() {
    // HACK: fix for problem whereby resizing sidebar causes scrollTop to jump to 0
    if (this._wrapper.scrollTop === 0 && this.props.scrollPosition.top > 200) {
      this._wrapper.scrollTop = this.props.scrollPosition.top;
    }

    // let parent know the new positions of the scroll
    this.props.onScroll({
      left: this._wrapper.scrollLeft,
      top: this._wrapper.scrollTop,
    });
  }

  // eslint-disable-next-line react/no-unused-class-component-methods
  getTableWidth(schema, table) {
    const index = this.state.tables.findIndex(
      (t) => t.schema() === schema && t.table() === table,
    );
    if (index < 0) {
      return 0;
    }
    const rect = this._boxes[index].getBoundingClientRect();
    return rect.width;
  }

  render() {
    const columnsToHighlight = {}; // e.g. public.orders.user_id => blue
    const boxes = this.renderBoxes(columnsToHighlight);
    const lines = this.renderLines(columnsToHighlight);

    const style = {
      background: this.props.demo
        ? "url('/assets/img/repeated-square.png')"
        : undefined,
      backgroundSize: '111px',
      width: this.props.gridWidth,
      height: this.props.gridHeight,
      transform: `scale(${this.props.scale})`,
      transformOrigin: 'top left', // note: we no longer change this, as it made the screen jump about when scrolling
      cursor: this.state.selected === -1 ? 'auto' : 'grab',
    };

    return (
      <div
        className={css.mapContainerInner}
        ref={(div) => (this._wrapper = div)}
        onScroll={(e) => this.onScroll(e)}
      >
        <div
          className={`${css.root} gridBackground`}
          style={style}
          ref={(div) => (this._container = div)}
          onTouchMove={(e) => this.onMouseMove(e.touches[0])}
          onMouseMove={(e) => this.onMouseMove(e)}
          onTouchEnd={(e) => this.onMouseUp(e.touches[0])}
          onMouseUp={(e) => this.onMouseUp(e)}
        >
          <svg
            className={css.svg}
            style={{
              width: this.props.gridWidth,
              height: this.props.gridHeight,
            }}
          >
            {this.renderArrows()}
            {lines}
          </svg>
          {/* <div style={this.props.redBox}></div> */}
          {boxes}
        </div>
      </div>
    );
  }
}

export default Map;
